import {
  Dialog,
  type DialogProps,
  Label,
  Select,
  Textarea,
} from '@sr/ui-library';
import { useEffect, useRef, useState } from 'react';
import { msr3ReturnBannerCopy } from '../constant/msr3ReturnBannerCopy';
import { pushDataLayerEvent } from '../utils/analytics/dataLayer';
import { submitMsr3ReturnFeedback } from '../utils/hubspotFormSubmission';

const MSR3_RETURN_EVENT = 'msr3_return_to_msr2';

export const MSR3_RETURN_REASON_NOT_PROVIDED = 'not_provided';

export type ReturnToMsr2DialogProps = Pick<
  DialogProps,
  'heading' | 'onOpenChange' | 'open'
> & {
  body: string;
  email: string | undefined;
  /** Closing line, rendered under the fields and above the buttons. */
  footer: string;
  returnUrl: string;
};

export const ReturnToMsr2Dialog = ({
  body,
  email,
  footer,
  heading,
  onOpenChange,
  open,
  returnUrl,
}: ReturnToMsr2DialogProps) => {
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Only the newest attempt may navigate. A submission stays in flight for up
  // to SUBMIT_TIMEOUT_MS, during which the member can cancel, reopen and submit
  // again — without this the first attempt's resolution would navigate and
  // unload the page while the second one's request was still being dispatched.
  const attempt = useRef(0);
  // Not derived from `isSending`: state updates are not synchronous, so only a
  // ref can reject a second click in the same tick.
  const isSubmitting = useRef(false);
  // The shell remounts on navigation, so a pending attempt can outlive this
  // component — e.g. the member presses browser Back, which the modal does not
  // intercept. It must not drag them to MSR2 afterwards.
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const invalidateInFlightAttempt = () => {
    attempt.current += 1;
    isSubmitting.current = false;
    setIsSending(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    // Closing means they changed their mind: abandon any attempt still in
    // flight and drop the answers so a later visit starts clean.
    if (!nextOpen) {
      invalidateInFlightAttempt();
      setReason('');
      setFeedback('');
    }

    onOpenChange?.(nextOpen);
  };

  const handleReturn = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const thisAttempt = attempt.current;

    setIsSending(true);

    // Navigating first would cancel the submission's CORS preflight before the
    // POST is dispatched. Never blocks the member: this resolves false rather
    // than throwing, and times out on its own.
    await submitMsr3ReturnFeedback({ email, feedback, reason });

    // Cancelled, superseded by a later attempt, or the shell remounted.
    if (!isMounted.current || thisAttempt !== attempt.current) return;

    // Counted here rather than on click so the metric means "returned", not
    // "considered returning". Reported even when blank, so the share of members
    // who skip the question stays visible.
    pushDataLayerEvent({
      event: MSR3_RETURN_EVENT,
      reason: reason || MSR3_RETURN_REASON_NOT_PROVIDED,
    });

    window.location.assign(returnUrl);
  };

  return (
    <Dialog
      heading={heading}
      actionButtonProps={{
        children: isSending
          ? msr3ReturnBannerCopy.confirm.sendingLabel
          : msr3ReturnBannerCopy.confirm.submitLabel,
        disabled: isSending,
        loading: isSending,
        onClick: handleReturn,
      }}
      cancelButtonProps={{
        children: msr3ReturnBannerCopy.confirm.cancelLabel,
      }}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <div className="flex flex-col gap-4">
        <p className="whitespace-pre-line">{body}</p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="msr3-return-reason">
            {msr3ReturnBannerCopy.feedback.reasonLabel}
          </Label>
          <Select
            id="msr3-return-reason"
            options={[...msr3ReturnBannerCopy.reasons]}
            placeholder={msr3ReturnBannerCopy.feedback.reasonPlaceholder}
            // Both this and the dialog portal to body, and SelectContent's own
            // z-50 sits under the dialog's z-[1000].
            selectContentProps={{ className: 'z-[1001]' }}
            sorting="none"
            value={reason}
            onValueChange={setReason}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="msr3-return-feedback">
            {msr3ReturnBannerCopy.feedback.textLabel}
          </Label>
          <Textarea
            id="msr3-return-feedback"
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
          />
        </div>

        <p className="whitespace-pre-line">{footer}</p>
      </div>
    </Dialog>
  );
};
